import {Injectable,} from '@angular/core';
import {environment,} from '@environments/environment';
import {LogLevel,} from '@models/index';
import {MessageProvider, StringUtil,} from '@services/index';

/**
 * ロガーサービス。
 */
@Injectable({
  providedIn: 'root',
})
export class Logger {
  envLogLevel = environment.log_level;

  constructor(
    private messageProvider: MessageProvider,
    private stringUtil: StringUtil
  ) {}

  /**
   * [一般開発者利用可]
   * debugメッセージをコンソールに出力する。
   * 環境設定ファイル(environment)に定義されているログレベルに応じて出力するか判断する。
   * optionalParamsはconsole.logの第二引数に設定される。
   * @param message 出力メッセージ
   * @param optionalParams メッセージに加えて出力させたいパラメータ
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  debug(message?: any, ...optionalParams: any[]): void {
    if (this.envLogLevel <= LogLevel.DEBUG && !this.stringUtil.isEmpty(message)) {
      if (optionalParams && optionalParams.length >= 1) {
        console.log(`${this.getCurrentDateTimeString()} DEBUG ${message}`, optionalParams);
      } else {
        console.log(`${this.getCurrentDateTimeString()} DEBUG ${message}`);
      }
    }
  }

  /**
   * [一般開発者利用可]
   * infoをコンソールに出力する。
   * 環境設定ファイル(environment)に定義されているログレベルに応じて出力するか判断する。
   * @param messageId 出力メッセージID
   * @param messageParams メッセージに埋め込むパラメータ
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  info(messageId: string, ...messageParams: any[]): void {
    const message = this.messageProvider.getMessageString({messageId, messageParams,}, true);
    if (this.envLogLevel <= LogLevel.INFO && !this.stringUtil.isEmpty(message)) {
      console.info(`${this.getCurrentDateTimeString()} INFO ${message}`);
    }
  }

  /**
   * [一般開発者利用可]
   * warningをコンソールに出力する。
   * 環境設定ファイル(environment)に定義されているログレベルに応じて出力するか判断する。
   * @param messageId 出力メッセージID
   * @param messageParams メッセージに埋め込むパラメータ
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  warn(messageId: string, ...messageParams: any[]): void {
    const message = this.messageProvider.getMessageString({messageId, messageParams,}, true);
    if (this.envLogLevel <= LogLevel.WARN && !this.stringUtil.isEmpty(message)) {
      console.warn(`${this.getCurrentDateTimeString()} WARN ${message}`);
    }
  }

  /**
   * [一般開発者利用可]
   * errorをコンソールに出力する。
   * 環境設定ファイル(environment)に定義されているログレベルに応じて出力するか判断する。
   * @param messageId 出力メッセージID
   * @param messageParams メッセージに埋め込むパラメータ
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  error(messageId: string, ...messageParams: any[]): void {
    const message = this.messageProvider.getMessageString({messageId, messageParams,}, true);
    if (this.envLogLevel <= LogLevel.ERROR && !this.stringUtil.isEmpty(message)) {
      console.error(`${this.getCurrentDateTimeString()} ERROR ${message}`);
    }
  }

  /**
   * [一般開発者利用可(予期できないエラーを出力するときのみ使用)]
   * フリーテキストのerrorをコンソールに出力する。
   * 環境設定ファイル(environment)に定義されているログレベルに応じて出力するか判断する。
   * @param message 出力メッセージ
   * @param optionalParams メッセージに加えて出力させたいパラメータ（consoleに出力するオブジェクトで、いかなるオブジェクトでもよい）
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  errorWithFreeText(message?: any, ...optionalParams: any[]): void {
    if (this.envLogLevel <= LogLevel.ERROR && !this.stringUtil.isEmpty(message)) {
      if (optionalParams && optionalParams.length >= 1) {
        console.error(`${this.getCurrentDateTimeString()} ERROR ${message}`, optionalParams);
      } else {
        console.error(`${this.getCurrentDateTimeString()} ERROR ${message}`);
      }
    }
  }

  /**
   * [一般開発者利用不可]
   * 現在日時取得
   * @returns String(YYYY-MM-DD HH:mm:ss.SSS)
   */
  private getCurrentDateTimeString(): string {
    const now = new Date();
    const year = now.getFullYear().toString().padStart(4, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const date = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const milliseconds = now.getMilliseconds().toString().padStart(3, '0');
    return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  }
}
